import type gsap from "gsap";
import type { HistoricalTimelineDecade, HistoricalTimelineEvent } from "../generated-types";
import {
	createEmptyYearsStateElement,
	getDecadeDetailCardMarkup,
	getDetailCardFactsMarkup,
	getFactsMarkup,
	type DecadeColumnLabels,
} from "./decade-column-markup";
import { createYearDots } from "./create-year-dots";
import { bindDetailDotHover } from "./detail-dot-animations";
import { renderDecadeHoverGraphsHost } from "./decade-hover-graph-animations";
import {
	destroyDetailYearSwiper,
	initDetailYearSwiper,
	slideDetailYearTo,
	type DetailYearRowElement,
} from "./detail-year-swiper";
import {
	destroyDetailEventImageSwipers,
	initDetailEventImageSwipers,
} from "./detail-event-image-swiper";
import {
	animateDetailEventSlide,
	animateDetailEventSlideClose,
	resetDetailEventHostStyles,
	runDetailEntranceAnimations,
	resetDetailCardEntranceStyles,
} from "./detail-view-animations";
import { DETAIL_ENTRANCE_ROW_SCATTER_STAGGER_S } from "./timeline-constants";
import { getTimelineEventControlId, getTimelineEventPanelId, getEventImages, getYearGroups, getDecadeYearRange, escapeHtmlAttribute, timelineYearNamesMatch, type YearGroup } from "./timeline-utils";
import { resolveDetailSelection, type TimelineDetailSelection } from "./timeline-url-state";

const COPY_TOAST_DURATION_MS = 2000;
const COPY_TOAST_TRANSITION_MS = 350;

function showCopiedToast(button: HTMLElement, copiedLabel = "Copied"): void {
	const wrap = button.closest<HTMLElement>(".historical-timeline__detail-copy-wrap");
	if (!wrap) {
		return;
	}

	const existingTimer = wrap.dataset.copyToastTimer;
	if (existingTimer) {
		window.clearTimeout(Number(existingTimer));
		delete wrap.dataset.copyToastTimer;
	}

	wrap.querySelector(".historical-timeline__copy-toast")?.remove();

	const toast = document.createElement("div");
	toast.className = "historical-timeline__copy-toast";
	toast.textContent = copiedLabel;
	toast.setAttribute("role", "status");
	wrap.appendChild(toast);

	// Double rAF ensures the initial hidden state is painted before transitioning in.
	requestAnimationFrame(() => {
		requestAnimationFrame(() => {
			toast.classList.add("is-visible");
		});
	});

	const hideTimer = window.setTimeout(() => {
		toast.classList.remove("is-visible");
		window.setTimeout(() => toast.remove(), COPY_TOAST_TRANSITION_MS);
		delete wrap.dataset.copyToastTimer;
	}, COPY_TOAST_DURATION_MS);

	wrap.dataset.copyToastTimer = String(hideTimer);
}

export type DecadeDetailLabels = DecadeColumnLabels & {
	close?: string;
	copyLink?: string;
	copied?: string;
	prev?: string;
	next?: string;
};

export type DecadeDetailViewController = {
	element: HTMLElement;
	update: (options: {
		decade: HistoricalTimelineDecade;
		activeCategoryKeys: string[];
		withEntrance?: boolean;
	}) => void;
	getState: () => TimelineDetailSelection;
	destroy: () => void;
};

export type DecadeDetailViewOptions = {
	initialActiveCategoryKeys?: string[];
	initialSelection?: {
		yearName?: string;
		eventIndex?: number;
	};
	onStateChange?: (state: TimelineDetailSelection) => void;
	getShareUrl?: () => string;
};

function getDetailNavButtonMarkup(
	direction: "prev" | "next",
	label: string,
	compact = false
): string {
	const compactClass = compact ? " historical-timeline__detail-nav-button--compact" : "";

	return `
		<button
			type="button"
			class="historical-timeline__detail-nav-button swiper-button-${direction} historical-timeline__detail-nav-button--${direction}${compactClass}"
			aria-label="${escapeHtmlAttribute(label)}"
		>
			<span class="historical-timeline__detail-nav-icon historical-timeline__detail-nav-icon--${direction}" aria-hidden="true"></span>
		</button>
	`.trim();
}

function getEventImageSlideMarkup(imageUrl: string, imageAlt: string): string {
	return `
		<div class="swiper-slide historical-timeline__detail-event-media-slide">
			<div class="historical-timeline__detail-event-image-wrap">
				<img
					class="historical-timeline__detail-event-image"
					src="${escapeHtmlAttribute(imageUrl)}"
					alt="${imageAlt}"
					loading="lazy"
				/>
			</div>
		</div>
	`.trim();
}

function getEventImageCaptionMarkup(caption: string | undefined, isActive: boolean): string {
	if (!caption) {
		return "";
	}

	const activeClass = isActive ? " is-active" : "";

	return `<p class="historical-timeline__detail-event-caption${activeClass}">${escapeHtmlAttribute(caption)}</p>`;
}

function getEventImageMarkup(event: HistoricalTimelineEvent, labels: DecadeDetailLabels): string {
	const images = getEventImages(event);

	if (images.length === 0) {
		return "";
	}

	const imageAlt = escapeHtmlAttribute(event.name ?? "");

	if (images.length === 1) {
		const [{ imageUrl, caption }] = images;

		return `
			<figure class="historical-timeline__detail-event-media">
				<div class="historical-timeline__detail-event-image-wrap">
					<img
						class="historical-timeline__detail-event-image"
						src="${escapeHtmlAttribute(imageUrl)}"
						alt="${imageAlt}"
						loading="lazy"
					/>
				</div>
				${
					caption
						? `<figcaption class="historical-timeline__detail-event-caption">${escapeHtmlAttribute(caption)}</figcaption>`
						: ""
				}
			</figure>
		`.trim();
	}

	const slidesMarkup = images
		.map(({ imageUrl }) => getEventImageSlideMarkup(imageUrl, imageAlt))
		.join("");
	const captionsMarkup = images
		.map(({ caption }, index) => getEventImageCaptionMarkup(caption, index === 0))
		.join("");

	return `
		<div class="historical-timeline__detail-event-media">
			<div class="historical-timeline__detail-event-media-swiper-wrap">
				<div class="historical-timeline__detail-event-media-image-stage">
					<div class="swiper historical-timeline__detail-event-media-swiper">
						<div class="swiper-wrapper">
							${slidesMarkup}
						</div>
					</div>
					<div class="historical-timeline__detail-event-media-nav">
						${getDetailNavButtonMarkup("prev", labels.prev ?? "Previous", true)}
						${getDetailNavButtonMarkup("next", labels.next ?? "Next", true)}
					</div>
				</div>
				${captionsMarkup ? `<div class="historical-timeline__detail-event-media-captions">${captionsMarkup}</div>` : ""}
			</div>
		</div>
	`.trim();
}

function getEventCardMarkup(event: HistoricalTimelineEvent, labels: DecadeDetailLabels): string {
	const factsMarkup = getFactsMarkup(
		event.pricePerBarrel,
		event.population,
		labels
	);

	return `
		<article class="historical-timeline__detail-event-card">
			<div class="historical-timeline__detail-event-body">
				<div class="historical-timeline__detail-event-copy">
					<h3 class="historical-timeline__detail-event-title">${event.name ?? ""}</h3>
					<p class="historical-timeline__detail-event-description">${event.description ?? ""}</p>
					<div class="historical-timeline__detail-event-facts">${factsMarkup}</div>
					<div class="historical-timeline__detail-copy-wrap">
						<button type="button" class="historical-timeline__detail-copy-link" data-copy-link>
							${labels.copyLink ?? "Copy link"}
						</button>
					</div>
				</div>
				${getEventImageMarkup(event, labels)}
			</div>
		</article>
	`.trim();
}

function getEventSwiperMarkup(
	dotItems: YearGroup["dotItems"],
	labels: DecadeDetailLabels,
	yearName: string
): string {
	const slidesMarkup = dotItems
		.map(
			(dotItem, index) =>
				`<div
					class="swiper-slide historical-timeline__detail-event-slide"
					id="${getTimelineEventPanelId(yearName, index)}"
					role="region"
					aria-labelledby="${getTimelineEventControlId(yearName, index)}"
				>${getEventCardMarkup(dotItem.event, labels)}</div>`
		)
		.join("");

	return `
		<div class="swiper historical-timeline__detail-event-swiper">
			<div class="swiper-wrapper">
				${slidesMarkup}
			</div>
		</div>
	`.trim();
}

function getSingleEventSlideMarkup(
	dotItem: YearGroup["dotItems"][number],
	labels: DecadeDetailLabels,
	yearName: string,
	eventIndex: number
): string {
	return `
		<div
			class="historical-timeline__detail-event-slide"
			id="${getTimelineEventPanelId(yearName, eventIndex)}"
			role="region"
			aria-labelledby="${getTimelineEventControlId(yearName, eventIndex)}"
		>${getEventCardMarkup(dotItem.event, labels)}</div>
	`.trim();
}

function bindEventCopyLinks(container: HTMLElement, getShareUrl: () => string, copiedLabel = "Copied"): void {
	container.querySelectorAll<HTMLButtonElement>("[data-copy-link]").forEach((copyButton) => {
		copyButton.addEventListener("click", async () => {
			try {
				await navigator.clipboard.writeText(getShareUrl());
				showCopiedToast(copyButton, copiedLabel);
			} catch {
				// Clipboard unavailable
			}
		});
	});
}

function renderYearRow(
	yearGroup: YearGroup,
	isFirst: boolean,
	isSelected: boolean,
	eventIndex: number,
	labels: DecadeDetailLabels,
	onSelectYear: () => void,
	onSelectEvent: (index: number) => void,
	getShareUrl: () => string,
	entranceToken?: number,
	rowIndex = 0
): HTMLElement {
	const row = document.createElement("li");
	row.className = `historical-timeline__detail-year${isFirst ? " historical-timeline__detail-year--first" : ""}`;
	row.dataset.yearName = yearGroup.yearName;

	const rowMain = document.createElement("div");
	rowMain.className = `historical-timeline__detail-year-main${
		isSelected && yearGroup.dotItems.length > 1 ? " historical-timeline__detail-year-main--expanded" : ""
	}`;

	rowMain.addEventListener("click", (event) => {
		if (!(event.target instanceof Element)) {
			return;
		}

		if (
			event.target.closest(".historical-timeline__dot") ||
			event.target.closest(".historical-timeline__detail-year-nav") ||
			event.target.closest(".historical-timeline__detail-year-label")
		) {
			return;
		}

		onSelectYear();
	});

	const yearButton = document.createElement("button");
	yearButton.type = "button";
	yearButton.className = "historical-timeline__detail-year-label";
	yearButton.textContent = yearGroup.yearName;
	yearButton.addEventListener("click", onSelectYear);
	rowMain.appendChild(yearButton);

	const detailRow = row as HTMLElement & { _dotsHoverCleanup?: () => void };

	const { element: dotsElement, cleanup: dotsCleanup, dotsList } = createYearDots({
		dotItems: yearGroup.dotItems,
		layout: "row",
		size: "lg",
		activeIndex: isSelected ? eventIndex : undefined,
		onDotClick: onSelectEvent,
		scatterToken: entranceToken,
		scatterBaseDelay: rowIndex * DETAIL_ENTRANCE_ROW_SCATTER_STAGGER_S,
		getDotId: (dotIndex) => dotIndex,
		listClassName: "historical-timeline__detail-year-dots",
		yearName: yearGroup.yearName,
		eventLabel: labels.event,
		eventsInYear: labels.eventsInYear,
		onScatterComplete: () => {
			if (!dotsList) {
				return;
			}

			dotsList.classList.add("is-detail-dots-ready");
			detailRow._dotsHoverCleanup?.();
			detailRow._dotsHoverCleanup = bindDetailDotHover(dotsList);
		},
	});

	rowMain.appendChild(dotsElement);

	if (isSelected && yearGroup.dotItems.length > 1) {
		const nav = document.createElement("div");
		nav.className = "historical-timeline__detail-year-nav";

		const prevButton = document.createElement("button");
		prevButton.type = "button";
		prevButton.className =
			"historical-timeline__detail-nav-button swiper-button-prev historical-timeline__detail-nav-button--prev";
		prevButton.setAttribute("aria-label", labels.prev ?? "Previous");
		prevButton.innerHTML = `<span class="historical-timeline__detail-nav-icon historical-timeline__detail-nav-icon--prev" aria-hidden="true"></span>`;

		const nextButton = document.createElement("button");
		nextButton.type = "button";
		nextButton.className =
			"historical-timeline__detail-nav-button swiper-button-next historical-timeline__detail-nav-button--next";
		nextButton.setAttribute("aria-label", labels.next ?? "Next");
		nextButton.innerHTML = `<span class="historical-timeline__detail-nav-icon historical-timeline__detail-nav-icon--next" aria-hidden="true"></span>`;

		nav.appendChild(prevButton);
		nav.appendChild(nextButton);
		rowMain.appendChild(nav);
	}

	row.appendChild(rowMain);

	const divider = document.createElement("div");
	divider.className = "historical-timeline__detail-year-divider";
	row.appendChild(divider);

	if (isSelected && yearGroup.dotItems.length > 0) {
		const eventHost = document.createElement("div");
		eventHost.className = "historical-timeline__detail-event-host";

		if (yearGroup.dotItems.length > 1) {
			eventHost.innerHTML = getEventSwiperMarkup(yearGroup.dotItems, labels, yearGroup.yearName);
			eventHost.querySelectorAll<HTMLElement>(".historical-timeline__detail-event-slide").forEach((slide, index) => {
				if (index !== eventIndex) {
					slide.hidden = true;
				}
			});
		} else {
			eventHost.innerHTML = getSingleEventSlideMarkup(
				yearGroup.dotItems[0],
				labels,
				yearGroup.yearName,
				eventIndex
			);
		}

		bindEventCopyLinks(eventHost, getShareUrl, labels.copied);

		row.appendChild(eventHost);

		const closingDivider = document.createElement("div");
		closingDivider.className =
			"historical-timeline__detail-year-divider historical-timeline__detail-year-divider--after-slide";
		row.appendChild(closingDivider);
	}

	row.dataset.dotsCleanup = "true";
	(row as HTMLElement & { _dotsCleanup?: () => void })._dotsCleanup = dotsCleanup;

	return row;
}

export function createDecadeDetailView(
	decade: HistoricalTimelineDecade,
	labels: DecadeDetailLabels,
	onClose: () => void,
	onResetFilters: () => void,
	options: DecadeDetailViewOptions = {}
): DecadeDetailViewController {
	let decadeData = decade;
	let activeCategoryKeys = options.initialActiveCategoryKeys ?? [];
	const initialSelection = resolveDetailSelection(
		decadeData,
		activeCategoryKeys,
		options.initialSelection?.yearName,
		options.initialSelection?.eventIndex
	);
	let selectedYearName: string | null = initialSelection.yearName;
	let selectedEventIndex = initialSelection.eventIndex;
	let detailEntranceToken = 1;
	let detailAnimationTweens: gsap.core.Animation[] = [];
	let detailGraphDrawTween: gsap.core.Timeline | undefined;
	let isYearTransitionActive = false;
	const getShareUrl = options.getShareUrl ?? (() => window.location.href);

	const notifyStateChange = () => {
		options.onStateChange?.({
			yearName: selectedYearName,
			eventIndex: selectedEventIndex,
		});
	};

	const killDetailAnimations = () => {
		isYearTransitionActive = false;
		yearsHost.classList.remove("is-year-transitioning");
		resetDetailCardEntranceStyles(card);
		resetDetailEventHostStyles(
			yearsHost.querySelector<HTMLElement>(".historical-timeline__detail-event-host")
		);
		detailGraphDrawTween?.kill();
		detailGraphDrawTween = undefined;
		detailAnimationTweens.forEach((tween) => tween.kill());
		detailAnimationTweens = [];
	};

	const runPostRenderAnimations = ({
		withEntrance,
		animateYearHeight,
		animateEventContent,
		updateCard,
	}: {
		withEntrance: boolean;
		animateYearHeight: boolean;
		animateEventContent: boolean;
		updateCard: boolean;
	}) => {
		requestAnimationFrame(() => {
			if (withEntrance) {
				detailAnimationTweens.push(
					...runDetailEntranceAnimations(card, yearsHost, { animateCard: updateCard })
				);
			}

			const eventHost = yearsHost.querySelector<HTMLElement>(".historical-timeline__detail-event-host");

			if (animateEventContent || animateYearHeight) {
				if (eventHost) {
					detailAnimationTweens.push(
						...animateDetailEventSlide(eventHost, {
							animateHeight: animateYearHeight,
							activeSlideIndex: selectedEventIndex,
						})
					);
				}
			}
		});
	};

	const root = document.createElement("section");
	root.className = "historical-timeline__detail";
	root.setAttribute("aria-labelledby", "historical-timeline-detail-decade-label");

	const grid = document.createElement("div");
	grid.className = "historical-timeline__detail-grid";

	const card = document.createElement("div");
	card.className = "historical-timeline__detail-card";

	const yearsHost = document.createElement("ol");
	yearsHost.className = "historical-timeline__detail-years";

	grid.appendChild(card);
	grid.appendChild(yearsHost);
	root.appendChild(grid);

	const handleCloseClick = (event: Event) => {
		const target = event.target;
		if (!(target instanceof Element)) {
			return;
		}

		if (target.closest(".historical-timeline__detail-close")) {
			onClose();
		}
	};

	card.addEventListener("click", handleCloseClick);

	const handleKeyDown = (event: KeyboardEvent) => {
		if (event.key === "Escape") {
			onClose();
		}
	};

	root.addEventListener("keydown", handleKeyDown);

	const cleanupYearRows = () => {
		yearsHost
			.querySelectorAll<DetailYearRowElement & { _dotsCleanup?: () => void; _dotsHoverCleanup?: () => void }>(
				".historical-timeline__detail-year"
			)
			.forEach((row) => {
				const eventHost = row.querySelector<HTMLElement>(".historical-timeline__detail-event-host");
				if (eventHost) {
					destroyDetailEventImageSwipers(eventHost);
				}

				destroyDetailYearSwiper(row);
				row._dotsHoverCleanup?.();
				row._dotsCleanup?.();
			});
		yearsHost.replaceChildren();
	};

	const getSelectedYearRow = (): DetailYearRowElement | null => {
		if (!selectedYearName) {
			return null;
		}

		const rows = yearsHost.querySelectorAll<DetailYearRowElement>(".historical-timeline__detail-year");
		for (const row of rows) {
			if (timelineYearNamesMatch(row.dataset.yearName, selectedYearName)) {
				return row;
			}
		}

		return null;
	};

	const initSelectedYearSwiper = () => {
		const selectedRow = getSelectedYearRow();
		if (!selectedRow) {
			return;
		}

		initDetailYearSwiper(selectedRow, {
			initialIndex: selectedEventIndex,
			onSlideChange: (index) => {
				selectedEventIndex = index;
				notifyStateChange();
			},
		});

		const eventHost = selectedRow.querySelector<HTMLElement>(".historical-timeline__detail-event-host");
		if (eventHost) {
			initDetailEventImageSwipers(eventHost);
		}
	};

	const renderCard = ({ animateGraphs = false }: { animateGraphs?: boolean } = {}) => {
		const yearRange = getDecadeYearRange(decadeData);
		const isSingleYear =
			(decadeData.years ?? []).map((year) => year.name?.trim()).filter(Boolean).length === 1;

		card.innerHTML = getDecadeDetailCardMarkup(
			decadeData,
			labels,
			labels.close ?? "Close",
			yearRange,
			isSingleYear
		);

		const graphsHost = card.querySelector<HTMLElement>("[data-decade-graphs-host]");
		const factsHost = card.querySelector<HTMLElement>("[data-decade-facts-host]");

		if (factsHost) {
			factsHost.innerHTML = getDetailCardFactsMarkup(decadeData, activeCategoryKeys, labels);
		}

		if (!graphsHost) {
			return;
		}

		detailGraphDrawTween?.kill();
		detailGraphDrawTween = renderDecadeHoverGraphsHost(
			graphsHost,
			decadeData,
			activeCategoryKeys,
			`historical-timeline-detail-graph-${decadeData.name ?? "decade"}`,
			animateGraphs
		);
	};

	const requestYearChange = (yearName: string, eventIndex: number) => {
		if (isYearTransitionActive) {
			return;
		}

		const isSameYear = timelineYearNamesMatch(selectedYearName, yearName);

		if (isSameYear) {
			selectedEventIndex = eventIndex;
			const selectedRow = getSelectedYearRow();
			if (selectedRow) {
				slideDetailYearTo(selectedRow, eventIndex);
			}
			notifyStateChange();
			return;
		}

		const closingHost = yearsHost.querySelector<HTMLElement>(".historical-timeline__detail-event-host");
		if (!closingHost) {
			selectedYearName = yearName;
			selectedEventIndex = eventIndex;
			renderYears({
				animateYearHeight: true,
				animateEventContent: true,
			});
			notifyStateChange();
			return;
		}

		isYearTransitionActive = true;
		yearsHost.classList.add("is-year-transitioning");

		const { timeline, complete } = animateDetailEventSlideClose(closingHost);
		detailAnimationTweens.push(timeline);

		void complete.then(() => {
			if (!isYearTransitionActive) {
				return;
			}

			isYearTransitionActive = false;
			yearsHost.classList.remove("is-year-transitioning");
			selectedYearName = yearName;
			selectedEventIndex = eventIndex;
			renderYears({
				animateYearHeight: true,
				animateEventContent: true,
			});
			notifyStateChange();
		});
	};

	const renderYears = ({
		updateCard = false,
		withEntrance = false,
		animateYearHeight = false,
		animateEventContent = false,
	}: {
		updateCard?: boolean;
		withEntrance?: boolean;
		animateYearHeight?: boolean;
		animateEventContent?: boolean;
	} = {}) => {
		const yearGroups = getYearGroups(decadeData, activeCategoryKeys);
		const scatterToken = withEntrance ? detailEntranceToken : undefined;
		const shouldAnimateYearHeight = animateYearHeight || withEntrance;
		const shouldAnimateEventContent = animateEventContent || withEntrance;

		killDetailAnimations();

		if (yearGroups.length === 0) {
			selectedYearName = null;
			selectedEventIndex = 0;
			cleanupYearRows();
			yearsHost.classList.add("is-empty");
			if (updateCard) {
				renderCard({ animateGraphs: withEntrance });
			}
			const emptyStateItem = document.createElement("li");
			emptyStateItem.className = "historical-timeline__detail-years-empty";
			emptyStateItem.appendChild(createEmptyYearsStateElement(onResetFilters, labels));
			yearsHost.appendChild(emptyStateItem);

			if (withEntrance && updateCard) {
				runPostRenderAnimations({
					withEntrance: true,
					animateYearHeight: shouldAnimateYearHeight,
					animateEventContent: shouldAnimateEventContent,
					updateCard: true,
				});
			}

			return;
		}

		if (!selectedYearName || !yearGroups.some((group) => timelineYearNamesMatch(group.yearName, selectedYearName))) {
			selectedYearName = yearGroups[0]?.yearName ?? null;
			selectedEventIndex = 0;
		}

		const selectedGroup = yearGroups.find((group) => timelineYearNamesMatch(group.yearName, selectedYearName));
		if (selectedGroup && selectedEventIndex >= selectedGroup.dotItems.length) {
			selectedEventIndex = 0;
		}

		cleanupYearRows();
		yearsHost.classList.remove("is-empty");
		if (updateCard) {
			renderCard({ animateGraphs: withEntrance });
		}

		yearGroups.forEach((yearGroup, index) => {
			const isSelected = timelineYearNamesMatch(yearGroup.yearName, selectedYearName);
			const row = renderYearRow(
				yearGroup,
				index === 0,
				isSelected,
				selectedEventIndex,
				labels,
				() => {
					requestYearChange(yearGroup.yearName, 0);
				},
				(eventIndex) => {
					requestYearChange(yearGroup.yearName, eventIndex);
				},
				getShareUrl,
				scatterToken,
				index
			);
			yearsHost.appendChild(row);
		});

		initSelectedYearSwiper();

		if (withEntrance || shouldAnimateYearHeight || shouldAnimateEventContent) {
			runPostRenderAnimations({
				withEntrance,
				animateYearHeight: shouldAnimateYearHeight,
				animateEventContent: shouldAnimateEventContent,
				updateCard,
			});
		}
	};

	const update = ({
		decade: nextDecade,
		activeCategoryKeys: nextActiveCategoryKeys,
		withEntrance = true,
	}: {
		decade: HistoricalTimelineDecade;
		activeCategoryKeys: string[];
		withEntrance?: boolean;
	}) => {
		decadeData = nextDecade;
		activeCategoryKeys = nextActiveCategoryKeys;

		if (withEntrance) {
			detailEntranceToken += 1;
		}

		renderYears({ updateCard: true, withEntrance });
		notifyStateChange();
	};

	renderYears({ updateCard: true, withEntrance: true });
	notifyStateChange();

	root.tabIndex = -1;

	return {
		element: root,
		update,
		getState: () => ({
			yearName: selectedYearName,
			eventIndex: selectedEventIndex,
		}),
		destroy: () => {
			root.removeEventListener("keydown", handleKeyDown);
			card.removeEventListener("click", handleCloseClick);
			killDetailAnimations();
			cleanupYearRows();
			root.remove();
		},
	};
}
